import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import Message from "../../../i18n/Message";
import Notify from "../../shared/Notify";

/**
 * Reason codes the backend's OAuth callback can send, mapped to what the user is told.
 *
 * The set is closed on purpose. The callback ends in a browser redirect, so anything it reports
 * travels through a query parameter — and the underlying failure text is not safe to put there,
 * since it can carry an authorization server's response body. The backend sends a code and keeps
 * the detail in its log; the wording lives here.
 */
export const OAUTH_ERROR_MESSAGES = {
  denied: "mcp_oauth_error_denied",
  expired_flow: "mcp_oauth_error_expired_flow",
  registration_mismatch: "mcp_oauth_error_registration_mismatch",
  discovery_failed: "mcp_oauth_error_discovery_failed",
  exchange_failed: "mcp_oauth_error_exchange_failed",
  misconfigured: "mcp_oauth_error_misconfigured",
  unknown_state: "mcp_oauth_error_unknown_state",
};

/**
 * Surfaces the result of an OAuth login once, then removes it from the URL.
 *
 * Mount this on every page the callback can land on. It used to live only on the server details
 * page, while failures redirected to the server list — which had no handler at all, so an OAuth
 * error produced no visible message anywhere.
 *
 * Stripping the params matters as much as showing the toast: without it a reload re-fires the
 * message, and "Connected successfully" reappearing on a page the user came back to hours later is
 * worse than saying nothing.
 */
const useOauthResultToast = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const shownRef = useRef(false);

  useEffect(() => {
    if (shownRef.current) return;
    const oauth = searchParams.get("oauth");
    if (oauth !== "success" && oauth !== "error") return;

    shownRef.current = true;
    if (oauth === "success") {
      Notify.success(<Message id="mcp_oauth_connected" />);
    } else {
      const reason = searchParams.get("reason");
      // An unrecognised code still gets a message: a backend that learns a new reason before the
      // frontend does should degrade to the generic text, not to silence.
      const messageId = OAUTH_ERROR_MESSAGES[reason] || "mcp_oauth_failed";
      Notify.error(<Message id={messageId} />);
    }

    // Only our own params; anything else on the URL belongs to whoever put it there.
    const next = new URLSearchParams(searchParams);
    next.delete("oauth");
    next.delete("reason");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
};

export default useOauthResultToast;
